/** Longest the socket is held open for pending work after the hidden grace period. */
export const WS_PENDING_HOLD_MAX_MS = 5 * 60_000;

/** Options for {@link createDeferredSuspend}. */
export interface DeferredSuspendOptions {
  /** Whether work is still waiting for a result; checked on request and on every notify. */
  isBusy: () => boolean;
  /** Registers a change listener and returns its unsubscribe, e.g. `Store.subscribe`. */
  subscribe: (listener: () => void) => () => void;
  suspend: () => void;
  maxHoldMs: number;
}

/**
 * Suspends right away when idle; otherwise waits until `isBusy()` turns false or
 * `maxHoldMs` elapses. `cancel()` drops a pending hold without suspending.
 */
export const createDeferredSuspend = ({
  isBusy,
  subscribe,
  suspend,
  maxHoldMs,
}: DeferredSuspendOptions) => {
  let unsubscribe: (() => void) | null = null;
  let holdTimer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    unsubscribe?.();
    unsubscribe = null;
    if (holdTimer) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
  };

  const release = () => {
    cancel();
    suspend();
  };

  const request = () => {
    if (unsubscribe) {
      return;
    }
    if (!isBusy()) {
      suspend();
      return;
    }
    unsubscribe = subscribe(() => {
      if (!isBusy()) {
        release();
      }
    });
    holdTimer = setTimeout(release, maxHoldMs);
  };

  return { request, cancel };
};
